import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  /** Loud states (nothing to do, waiting) lead with brand red; quiet ones stay neutral. */
  accent?: "primary" | "muted";
  children?: ReactNode;
}

/**
 * The one empty-state shell for the order queue, shared by the Dashboard's
 * no-match / no-pending / waiting states and the By-Time view's no-promise-times
 * state so padding, icon size, and copy scale cannot drift between them.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  accent = "muted",
  children,
}: EmptyStateProps) {
  const primary = accent === "primary";

  return (
    <div className="rounded-radius-card border border-border bg-card px-6 py-16 text-center">
      <div
        className={`mx-auto mb-4 flex items-center justify-center rounded-full ${
          primary ? "h-16 w-16 bg-action-primary/10" : "h-14 w-14 bg-muted"
        }`}
      >
        <Icon
          className={primary ? "h-8 w-8 text-action-primary" : "h-7 w-7 text-muted-foreground"}
          aria-hidden="true"
        />
      </div>
      <h3 className={`font-semibold text-foreground ${primary ? "text-xl" : "text-lg"}`}>{title}</h3>
      <p className="mx-auto mt-1 max-w-xs text-sm text-muted-foreground">{description}</p>
      {children && (
        <div className="mt-5 flex flex-col items-center justify-center gap-2 sm:flex-row">{children}</div>
      )}
    </div>
  );
}

export default EmptyState;
